import { useCallback, useEffect, useRef, useState } from 'react';
import { consumePendingRecord } from '../../layouts/navigateTo';
import '../backlog/backlog.css';
import './reagentprep.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormReportsMenu } from '../../components/FormReportsMenu';
import { useDeleteFlow, useSaveFlow } from '../../components/confirm';
import { useNotify } from '../../components/notifications';
import { ReagentListShell } from './ReagentListShell';
import { GridSearch } from '../../components/GridSearch';
import { useGridTextSearch } from './useGridTextSearch';
import { useNavActions } from '../../hooks/useNavActions';
import { DateRangeSelect } from '../modules/DateRangeSelect';
import { ClassifyDialog } from './ClassifyDialog';
import { PrepNotepadDialog } from './PrepNotepadDialog';
import { draftToDisplayRow, type PrepNotepadDraft } from './prepNotepadDraft';
import { opensPrepNotepadOnNew } from './prepForms';
import ReagentPrepDetailPage from './ReagentPrepDetailPage';
import { deleteReagentPreps, fetchComponents, fetchLookup, fetchReagentPreps, saveComponents, type PrepNotepadComponentDto } from './api';
import { REAGENT_RANGES, rangeCode, rangeDays, type ReagentDateFilter, type ReagentPrepComponent, type ReagentPrepListRow } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Reagent Preparation → Reagent Prep Log (nav `rgp-preplog`).
// Port of legacy ALPACA `ReagentPreparation_ListView`; spec §7.
//
// Columns and sort come straight from the legacy Model.xafml: LLT · Division ·
// PrepType · PrepName · ExpirationDate · PreparedBy · DatePrepared, sorted
// DatePrepared DESC then LLT DESC.
//
// Layout = the Department list (ReagentListShell, 2026-11-03). A row click opens
// the record read-only; the row pencil / Edit open it editable; New opens the
// Classify popup. Delete runs the shared useDeleteFlow (reason gate).
// ─────────────────────────────────────────────────────────────────────────────

const COLUMNS: AppColumn<ReagentPrepListRow>[] = [
  // The live XAF grid (owner screenshot 2026-10-23): LLT · PrepType · PrepName ·
  // Department · ExpirationDate · PreparedBy · DatePrepared — no Division.
  { field: 'llt',            header: 'LLT',            width: 120 },
  { field: 'prepType',       header: 'PrepType',       width: 110 },
  { field: 'prepName',       header: 'PrepName',       width: 220 },
  { field: 'department',     header: 'Department',     width: 130 },
  // Dates render through AppDataGrid's `type`, which uses the shared fmtLocal*
  // helpers — MM/DD/YYYY, 24-hour, never inline formatting (§14b.6 / C2).
  { field: 'expirationDate', header: 'ExpirationDate', width: 120, type: 'date' },
  { field: 'preparedBy',     header: 'PreparedBy',     width: 130 },
  { field: 'datePrepared',   header: 'DatePrepared',   width: 120, type: 'date' },
];

/** What the page is showing. `list` is the landing view (rule 10); a detail is
 *  reached only by New or by selecting a row. */
type Screen =
  | { kind: 'list' }
  | { kind: 'detail'; id: string | null; newPrepType?: number;
      /** A row click opens the record read-only (Department parity); Edit / New open it editable. */
      mode?: 'view' | 'edit' };

/**
 * The Prep Notepad's state, and WHY it is driven from here rather than from the
 * detail page: the Notepad opens on TWO different paths (spec §8 / §9), and on
 * one of them there is no detail page yet.
 *
 *   `create`  — Classify → New Prep → Chemical or Micro Media. XAF opens the
 *               Notepad FIRST, with no parent record; its OK then creates the
 *               header from the buffer and opens the header form in Edit.
 *   `existing`— the Chemistry / Micro header form's "Prep Notepad" button,
 *               editing the components of a record that already exists.
 *
 * Calibration never appears here: it is the one category with no Prep Notepad,
 * and Classify sends it straight to its header form.
 */
type Notepad =
  | { mode: 'create'; prepSelectType: number }
  | { mode: 'existing'; prepSelectType: number; prepId: string; components: ReagentPrepComponent[];
      /** Open on this component (a row click in the components grid). */
      startComponentId?: number | null;
      /** The header form is in View mode — the Notepad only browses. */
      viewOnly?: boolean };

export default function ReagentPrepLogPage({ onHome }: { onHome?: () => void }) {
  // MAC rights on this form: a role without Create / Write / Delete does not SEE New / Edit / Delete.
  const perm = useNavActions('rgp-preplog');
  const notify = useNotify();
  const runDelete = useDeleteFlow();
  const runSave = useSaveFlow();
  const [search, setSearch] = useState('');
  // The rows in the order the grid shows them — "Detail View" with nothing ticked opens the top one.
  const shownRef = useRef<ReagentPrepListRow[]>([]);
  const [screen, setScreen]   = useState<Screen>({ kind: 'list' });
  const [classify, setClassify] = useState(false);
  const [notepad, setNotepad] = useState<Notepad | null>(null);
  /** Bumped after the Notepad's OK writes an EXISTING prep's components. */
  const [componentsVersion, setComponentsVersion] = useState(0);
  /** A NEW prep's buffered components, shaped for the grid until the first Save. */
  const [pendingRows, setPendingRows] = useState<ReagentPrepComponent[] | null>(null);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [rows, setRows]       = useState<ReagentPrepListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs]   = useState<number | null>(null);
  // null until the first response tells us which filter the server applied —
  // the initial chip is the tenant default from Settings → Default Settings
  // (spec §7), so the page must not assume one before asking.
  const [dateFilter, setDateFilter] = useState<ReagentDateFilter | null>(null);
  // Deep-link from the QC Batch Others-tab ReagentID link: navigateToPage('rgp-preplog',
  // llt) parks the LLT so this form opens filtered to that reagent (user request
  // 2026-10-27). Consumed once on mount; loaded with 'All' so the LLT is found
  // regardless of date. A plain sidebar visit opens the full list (standing rule 10).
  const [lltFilter] = useState<string | null>(() => consumePendingRecord('rgp-preplog'));

  const load = useCallback(async (filter?: ReagentDateFilter) => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const res = await fetchReagentPreps(filter);
      setRows(res.rows);
      setDateFilter(res.dateFilter);
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to load reagent preparations.');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [notify]);

  // First load asks the server for the tenant default; later loads pass the chip.
  useEffect(() => { void load(lltFilter ? 'All' : undefined); }, [load, lltFilter]);

  /** Opens the record in ITS OWN form. Which of the three opens is decided by
   *  the row's prepSelectType, exactly as XAF's EditAction_Executing (:767-790)
   *  picks the DetailView. A row click opens it read-only; Edit and the row
   *  pencil open it editable (Department parity, 2026-11-03). */
  function openRow(row: ReagentPrepListRow, mode: 'view' | 'edit') {
    setScreen({ kind: 'detail', id: row.oid, newPrepType: row.prepSelectType ?? 0,
                mode: mode === 'edit' && perm.write ? 'edit' : 'view' });
  }

  /** Delete — the list action. Soft-deletes each header AND its components.
   *  The shared flow asks for the reason when Audit Trail Configuration requires one. */
  async function deleteSelected() {
    if (selected.size === 0) { notify.warning('Select at least one record to delete.'); return; }
    const ids = [...selected].map(String);
    await runDelete({
      formName: 'Reagent Prep Log',
      message: `Delete ${ids.length} preparation(s)? Their components are deleted with them.`,
      delete: async () => { await deleteReagentPreps(ids); },
      onDeleted: () => { setSelected(new Set()); void load(dateFilter ?? undefined); },
    });
  }

  /**
   * Classify → "New Prep" — THE per-category routing (spec §8 tab 1):
   *   Chemical / Micro Media → the Prep Notepad popup FIRST, no parent record.
   *   Calibration            → straight to its header form; it has no Notepad.
   */
  function startNewPrep(prepSelectType: number) {
    setClassify(false);
    // A fresh prep starts with NO buffered components — never one left over from a
    // prep that was cancelled (owner bug list #1, 2026-11-03).
    clearPending();
    if (opensPrepNotepadOnNew(prepSelectType)) setNotepad({ mode: 'create', prepSelectType });
    else setScreen({ kind: 'detail', id: null, newPrepType: prepSelectType });
  }

  /** Prep Notepad OK. On the CREATE path the header does not exist yet, so it
   *  is created from the current form defaults first, the buffer is attached,
   *  and the header form opens in Edit — legacy's §9.2 step 3. */
  async function notepadOk(rows: PrepNotepadDraft[]) {
    if (!notepad) return;
    // Draft → wire DTO. Ids, not names (the grid-lookups carry the Oid); the
    // two Exp Dates are omitted because the server derives them from the lot.
    const dto: PrepNotepadComponentDto[] = rows.map((d) => ({
      componentID: d.componentID,
      vendorStockId: d.vendorStockId || null,
      ltId: d.ltId || null,
      labStockId: d.labStockId || null,
      lltId: d.lltId || null,
      solvent: d.solvent,
      calculationApproachId: d.calculationApproachId || null,
      w1UnitsId: d.w1UnitsId || null,
      v1UnitsId: d.v1UnitsId || null,
      c1UnitsId: d.c1UnitsId || null,
      v2UnitsId: d.v2UnitsId || null,
      c2UnitsId: d.c2UnitsId || null,
      stockConc_C1: d.stockConc_C1 || null,
      volumeTaken_V1: d.volumeTaken_V1 || null,
      weight_g_w1: d.weight_g_w1 || null,
      finalVol_V2: d.finalVol_V2 || null,
      finalConc_C2: d.finalConc_C2 || null,
      purity: d.purity || null,
      density: d.density || null,
      mw: d.mw || null,
      eqWt: d.eqWt || null,
      constant: d.constant || null,
      formula: d.formula || null,
      comment: d.comment || null,
      phCriteria: d.phCriteria || null,
      ph: d.ph || null,
      autoclave: d.autoclave || null,
      filterSterilization: d.filterSterilization || null,
      sporeGrowth: d.sporeGrowth || null,
      positiveControl: d.positiveControl || null,
      negativeControl: d.negativeControl || null,
    }));

    // EXISTING prep: OK writes the components to the database, so it is a save and
    // runs through the shared save flow (standing rule 7, 2026-11-03) — "Do you want
    // to Save?", the reason prompt when configured, the blocking loader, then
    // "Saved successfully.". Cancel at the confirm leaves the Notepad open.
    if (notepad.mode === 'existing') {
      const { prepId, prepSelectType } = notepad;
      await runSave({
        formName: 'Reagent Prep Log',
        auditAction: 'edit',
        save: async () => { await saveComponents(prepId, dto); },
        onSaved: () => {
          setNotepad(null);
          // The detail page is already open on this id, so it is NOT remounted —
          // that would discard unsaved header edits. Bump the version so it re-reads
          // just the components and the grid shows what OK just wrote.
          setComponentsVersion((v) => v + 1);
          setScreen({ kind: 'detail', id: prepId, newPrepType: prepSelectType, mode: 'edit' });
          void load(dateFilter ?? undefined);
        },
        onError: (e) => notify.error(e instanceof Error ? e.message : 'Failed to save the components.'),
      });
      return;
    }

    try {
      // CREATE path: the header has no required fields filled yet, so it cannot
      // be persisted here (PrepType / PrepName / Expiration / Storage are all
      // required server-side). Legacy has the same shape — its new header is
      // unsaved until the user completes the form — so the buffer is carried
      // into the detail page and written on its first Save.
      setNotepad(null);
      setScreen({ kind: 'detail', id: null, newPrepType: notepad.prepSelectType });
      setPendingComponents(dto);
      // Show the buffered components in the grid now, before the first Save
      // writes them — with unit / approach NAMES, not ids.
      try {
        const [u, a] = await Promise.all([fetchLookup('units'), fetchLookup('approaches')]);
        const names = {
          units: new Map(u.map((o) => [o.id, o.name ?? ''] as const)),
          approaches: new Map(a.map((o) => [o.id, o.name ?? ''] as const)),
        };
        setPendingRows(rows.map((d) => draftToDisplayRow(d, names)));
      } catch {
        setPendingRows(rows.map((d) => draftToDisplayRow(d, { units: new Map(), approaches: new Map() })));
      }
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to save the components.');
    }
  }

  /** Components entered in the Notepad before the header existed; written by
   *  the detail page immediately after its first Save mints the record. */
  const [pendingComponents, setPendingComponents] = useState<PrepNotepadComponentDto[] | null>(null);

  /** Drop the new-prep Notepad buffer. It belongs to ONE unsaved prep, so it is
   *  cleared whenever that prep is left (Cancel / Close / List View), a new prep
   *  starts, or a copy opens. Before 2026-11-03 it survived a Cancel and the NEXT
   *  new prep — even a Calibration — saved those components with itself. */
  function clearPending() { setPendingComponents(null); setPendingRows(null); }

  const notepadDialog = notepad && (
    <PrepNotepadDialog
      // Keyed on the component it opens ON, so a click on another row re-opens
      // the Notepad there instead of keeping the first row's state.
      key={notepad.mode === 'existing' ? `${notepad.prepId}:${notepad.startComponentId ?? ''}` : 'create'}
      prepSelectType={notepad.prepSelectType}
      existing={notepad.mode === 'existing' ? notepad.components : []}
      startComponentId={notepad.mode === 'existing' ? notepad.startComponentId ?? null : null}
      viewOnly={notepad.mode === 'existing' && !!notepad.viewOnly}
      onOk={(rows) => void notepadOk(rows)}
      onClose={() => setNotepad(null)}
    />
  );

  const classifyDialog = classify && (
    <ClassifyDialog
      onNewPrep={startNewPrep}
      onCopied={(id) => {
        setClassify(false);
        clearPending();
        setScreen({ kind: 'detail', id });
        void load(dateFilter ?? undefined);
      }}
      onClose={() => setClassify(false)}
    />
  );

  // The row pencil opens the record editable (Department parity) — hidden for a role without Write.
  const columns: AppColumn<ReagentPrepListRow>[] = perm.write ? [{
    field: 'detailedit', header: '', noExport: true, sortable: false, filterable: false, width: 44, align: 'center',
    text: () => '',
    render: (r) => (
      <button type="button" className="pts-icon-btn edit" title="Edit" aria-label={`Edit ${r.llt ?? 'preparation'}`}
              onClick={(e) => { e.stopPropagation(); openRow(r, 'edit'); }}><i className="ti ti-edit" /></button>
    ),
  }, ...COLUMNS] : COLUMNS;

  // When deep-linked from a ReagentID, show only that LLT's preparations; then
  // the search box narrows what is shown (the selection lives on the full list).
  const displayRows = lltFilter
    ? rows.filter((r) => (r.llt ?? '').trim() === lltFilter.trim())
    : rows;
  const shownRows = useGridTextSearch(displayRows, columns, search);

  // ── Detail view (New or Edit) — replaces the list, never a popup (rule 12)
  if (screen.kind === 'detail') {
    return (
      <>
        <ReagentPrepDetailPage
          key={screen.id ?? `new-${screen.newPrepType}`}
          id={screen.id}
          initialMode={screen.mode ?? 'edit'}
          onHome={onHome}
          newPrepType={screen.newPrepType}
          pendingComponents={pendingComponents}
          pendingRows={pendingRows}
          componentsVersion={componentsVersion}
          onComponentsFlushed={clearPending}
          siblingIds={rows.map((r) => r.oid)}
          onNavigate={(nid, mode) => {
            const row = rows.find((r) => r.oid === nid);
            setScreen({ kind: 'detail', id: nid, newPrepType: row?.prepSelectType ?? 0, mode });
          }}
          onClose={() => { clearPending(); setScreen({ kind: 'list' }); }}
          // New on a DETAIL view reopens the Classify popup too (XAF :3810).
          onNew={() => setClassify(true)}
          onPrepNotepad={(prep, startComponentId, viewOnly) => {
            if (!prep.oid) return;
            void (async () => {
              // A failed read must SAY so — swallowed, a row click just did nothing.
              try {
                setNotepad({
                  mode: 'existing',
                  prepSelectType: prep.prepSelectType ?? 0,
                  prepId: prep.oid,
                  components: await fetchComponents(prep.oid),
                  startComponentId: startComponentId ?? null,
                  viewOnly: !!viewOnly,
                });
              } catch (e) {
                notify.error(e instanceof Error ? e.message : 'Failed to open the Prep Notepad.');
              }
            })();
          }}
          onCreated={(id) => setScreen({ kind: 'detail', id, newPrepType: screen.newPrepType, mode: 'edit' })}
          onChanged={() => void load(dateFilter ?? undefined)}
        />
        {classifyDialog}
        {notepadDialog}
      </>
    );
  }

  return (
    <ReagentListShell
      pageId="rgp-preplog" caption="Reagent Prep Log" auditFormName="Reagent Prep Log"
      loadMs={loadMs} loading={loading} onHome={onHome}
      // "Detail View" opens the ticked record — or the top one shown — read-only.
      viewToggle={{
        disabled: shownRows.length === 0,
        onDetail: () => {
          const id = selected.size === 1 ? String([...selected][0]) : (shownRef.current[0] ?? shownRows[0])?.oid;
          const row = rows.find((r) => r.oid === id);
          if (row) openRow(row, 'view');
        },
      }}
      // G5 bar, in G5's order (owner request 2026-11-03, side by side with the live
      // XAF screen): New · Reports ▾ · Edit · Delete · Refresh · Date Filter ·
      // Export to · Search. `newFirst` puts New ahead of the `left` slot (Reports),
      // the Date Filter combo rides in `beforeExport`, Search in `right`. The
      // hardcoded Prep Report / Preview Label buttons are hidden in legacy
      // (:71/:73) because the lab-assigned Reports ▾ supersedes them.
      toolbar={<AppToolbar
        newFirst
        // G5 tooltips (2026-11-03): "New Reagent Preparation", and XAF's selection hints on Edit / Delete.
        newTitle="New Reagent Preparation"
        selectionHints
        left={
          <FormReportsMenu
            reportsUrl="/api/v1/reagent-preps/reports?view=list"
            pdfUrl="/api/v1/reagent-preps/report-pdf"
            keysParam="llts"
            // G5 keeps Reports clickable with nothing selected (bug list #9, 2026-11-03).
            enabledWithoutSelection
            selectedKeys={rows.filter((r) => selected.has(r.oid) && r.llt).map((r) => r.llt!)}
          />
        }
        beforeExport={
          <DateRangeSelect
            variant="field" label="Date Filter" ranges={REAGENT_RANGES}
            value={dateFilter ? rangeDays(dateFilter) : null}
            onChange={(days) => { const c = rangeCode(days); setDateFilter(c); void load(c); }}
          />
        }
        right={<GridSearch variant="toolbar" value={search} onChange={setSearch} placeholder="Text to search..." title="Filter records by text" />}
        // New opens the Classify popup, NOT a blank record (XAF cancels the
        // default new-object behaviour, :3812).
        onNew={perm.create ? () => setClassify(true) : undefined}
        onEdit={perm.write ? () => {
          const row = rows.find((r) => r.oid === String([...selected][0]));
          if (row) openRow(row, 'edit');
        } : undefined}
        editDisabled={selected.size !== 1}
        onDelete={perm.delete ? () => void deleteSelected() : undefined}
        deleteDisabled={selected.size === 0}
        onRefresh={() => load(dateFilter ?? undefined)}
        refreshing={loading}
        exportData={{ filename: 'reagent-prep-log', ...gridExportMatrix(shownRows, columns) }}
      />}
    >
      <AppDataGrid<ReagentPrepListRow>
                  selectAllTitle="Selection state of all rows on all pages"
        rows={shownRows}
        columns={columns}
        getRowId={(r) => r.oid}
        loading={loading}
        selectable
        selectedIds={selected}
        onSelectionChange={setSelected}
        onVisibleRowsChange={(r) => { shownRef.current = r; }}
        // A click opens the record read-only; the row pencil / Edit open it editable.
        onRowClick={(r) => openRow(r, 'view')}
        emptyText={search ? 'No results match your search.'
          : lltFilter ? `No reagent preparation found for ${lltFilter}.` : 'No reagent preparations found for this date range.'}
      />
      {classifyDialog}
      {notepadDialog}
    </ReagentListShell>
  );
}
